import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import { applicationLogService } from '../../services/applicationLogService';

const ApplicationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    level: '',
    category: '',
    search: '',
    startDate: '',
    endDate: '',
    limit: 100
  });
  const [categories, setCategories] = useState([]);
  const [levels] = useState(['info', 'warning', 'error', 'critical']);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadLogs();
    loadCategories();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadLogs();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await applicationLogService?.getLogs(filters);
      setLogs(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await applicationLogService?.getLogCategories();
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      level: '',
      category: '',
      search: '',
      startDate: '',
      endDate: '',
      limit: 100
    });
  };

  const getLogLevelColor = (level) => {
    switch (level) {
      case 'info':
        return 'text-blue-600 bg-blue-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'critical':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getLogLevelIcon = (level) => {
    switch (level) {
      case 'info':
        return 'Info';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      case 'critical':
        return 'AlertOctagon';
      default:
        return 'Circle';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleString();
  };

  const categoryOptions = categories?.map(cat => ({
    value: cat,
    label: cat?.replace('_', ' ')?.toUpperCase()
  }));

  const levelOptions = levels?.map(level => ({
    value: level,
    label: level?.charAt(0)?.toUpperCase() + level?.slice(1)
  }));

  const limitOptions = [
    { value: 50, label: '50 application logs' },
    { value: 100, label: '100 application logs' },
    { value: 250, label: '250 application logs' },
    { value: 500, label: '500 application logs' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Application Logs</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Monitor application events and errors</p>
        </div>

        {/* Log Filters - Responsive */}
        <div className="mb-6 bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Search */}
            <div className="sm:col-span-2">
              <Input
                placeholder="Search application logs, grow logs, activities..."
                value={filters?.search}
                onChange={(e) => handleFilterChange('search', e?.target?.value)}
                className="w-full"
              />
            </div>

            {/* Level Filter */}
            <Select
              options={[{ value: '', label: 'All Levels' }, ...levelOptions]}
              value={filters?.level}
              onChange={(value) => handleFilterChange('level', value)}
              placeholder="Log Level"
            />

            {/* Category Filter */}
            <Select
              options={[{ value: '', label: 'All Categories' }, ...categoryOptions]}
              value={filters?.category}
              onChange={(value) => handleFilterChange('category', value)}
              placeholder="Category"
            />

            {/* Start Date */}
            <input
              type="date"
              value={filters?.startDate}
              onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
              className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
              max={new Date()?.toISOString()?.split('T')?.[0]}
            />

            {/* End Date */}
            <input
              type="date"
              value={filters?.endDate}
              onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
              className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
              max={new Date()?.toISOString()?.split('T')?.[0]}
            />

            {/* Limit */}
            <Select
              options={limitOptions}
              value={filters?.limit}
              onChange={(value) => handleFilterChange('limit', parseInt(value))}
              placeholder="Limit"
            />
          </div>

          {/* Quick Date Filters */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-500 mr-2">Quick filters:</span>
            {[
              { label: 'Today', days: 0 },
              { label: 'Last 7 days', days: 7 },
              { label: 'Last 30 days', days: 30 },
              { label: 'Last 90 days', days: 90 },
              { label: 'Last 6 months', days: 180 },
              { label: 'Last year', days: 365 },
              { label: 'Last 2 years', days: 730 }
            ]?.map((quick) => (
              <Button
                key={quick?.label}
                variant="outline"
                size="sm"
                onClick={() => {
                  const endDate = new Date();
                  const startDate = new Date();
                  startDate?.setDate(startDate?.getDate() - quick?.days);
                  
                  handleFilterChange('startDate', startDate?.toISOString()?.split('T')?.[0]);
                  handleFilterChange('endDate', endDate?.toISOString()?.split('T')?.[0]);
                }}
                className="text-xs"
              >
                {quick?.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Log Table - Horizontal scroll on mobile */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs?.map((log) => (
                  <tr key={log?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center space-x-2 px-2 py-1 rounded-full text-xs font-medium ${getLogLevelColor(log?.level)}`}>
                        <Icon name={getLogLevelIcon(log?.level)} size={12} />
                        <span>{log?.level?.toUpperCase()}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(log?.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log?.category?.replace('_', ' ')?.toUpperCase()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {log?.action}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log?.userFullName || 'System'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-md truncate">
                      {log?.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedLog(log)}
                      >
                        <Icon name="Eye" size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationLogs;